import { useState } from 'react';
import { StyleSheet } from 'react-native';
import { useMaskedInputProps } from 'react-native-mask-input';

import {
  FormControlTextInput,
  IconDollar,
  RadioGroup,
  Text,
  XStack,
  YStack,
  tokens,
} from '@fhs/ui';
import { useIsDesktopBp } from '@fhs-legacy/frontend/src/hooks/breakpoints';
import { useUIContext } from '@fhs-legacy/frontend/src/state/ui';
import { getCurrencyMask } from '@fhs-legacy/frontend/src/utils/masks';
import { CUSTOM_MONETARY_INPUT } from '@fhs-legacy/frontend/src/utils/test-ids';

import { MonetaryAmountOption } from './monetary-amount-option';

export type AmountOption = {
  value: string;
  label: string;
};

type MonetaryAmountOptionProps = {
  value: string;
  otherValue?: string;
  amountCents: number;
  amountOptions: AmountOption[];
  otherValueLabel?: string;
  errorMessage?: string;
  hideAmountPreview?: boolean;
  showHeaderRow?: boolean;
  amountPreviewTitle?: string;
  onSelect: (value: string) => void;
  onChangeOtherValue: (value: string) => void;
};

export function MonetaryAmountOptionSelector({
  amountOptions,
  value,
  otherValue,
  amountCents,
  errorMessage,
  hideAmountPreview = false,
  showHeaderRow = true,
  onSelect,
  onChangeOtherValue,
  otherValueLabel = 'Amount',
}: MonetaryAmountOptionProps) {
  const { currencySymbol, formatCurrencyForLocale } = useUIContext();

  // Make sure persisted value is formatted to apply the mask properly
  const initialAmountValue = otherValue ? otherValue.replace(/^0+/, '').padStart(3, '0') : '';

  const [maskedAmount, setMaskedAmount] = useState(initialAmountValue);

  const isDesktop = useIsDesktopBp();

  const { onChangeText, value: maskedValue } = useMaskedInputProps({
    value: maskedAmount,
    onChangeText: (_, unmasked) => {
      setMaskedAmount((+unmasked / 100).toFixed(2));
      onChangeOtherValue(unmasked);
    },
    mask: getCurrencyMask(currencySymbol ?? ''),
  });

  if (isDesktop) {
    return (
      <XStack style={styles.container}>
        <YStack style={styles.desktopLeftWrapper}>
          <XStack style={styles.tipSelectorDesktop}>
            {showHeaderRow && (
              <Text weight="semibold" style={styles.tipText}>
                Tips
              </Text>
            )}
            <RadioGroup style={styles.radioGroupDesktop} value={value} onChange={onSelect}>
              {amountOptions.map(tip => (
                <RadioGroup.Item value={tip.value} label={tip.label} key={tip.value}>
                  <MonetaryAmountOption text={tip.label} />
                </RadioGroup.Item>
              ))}
              <RadioGroup.Item value="other" label="Other">
                <MonetaryAmountOption text="Other" />
              </RadioGroup.Item>
            </RadioGroup>
          </XStack>
          {value === 'other' && (
            <YStack style={styles.inputContainerDesktop}>
              <Text weight="semibold">{otherValueLabel}</Text>
              <FormControlTextInput
                placeholder="e.g. $10.50"
                onChangeText={onChangeText}
                value={maskedValue}
                keyboardType="number-pad"
                rightIcon={<IconDollar size={24} color={tokens.colors.$blackOpacity30} />}
                maxLength={7}
                error={errorMessage}
              />
            </YStack>
          )}
        </YStack>
        {!hideAmountPreview && showHeaderRow && (
          <YStack style={styles.desltopRightWrapper}>
            <Text weight="semibold" style={styles.tipText}>
              + {formatCurrencyForLocale(amountCents)}
            </Text>
          </YStack>
        )}
      </XStack>
    );
  }

  return (
    <>
      {showHeaderRow && (
        <XStack style={styles.tipRow} testID="tip-amount">
          <Text weight="semibold" style={styles.tipText}>
            Tips
          </Text>
          <Text weight="semibold" style={styles.tipText}>
            + {formatCurrencyForLocale(amountCents)}
          </Text>
        </XStack>
      )}
      <YStack style={styles.container}>
        <RadioGroup style={styles.radioGroup} value={value} onChange={onSelect}>
          {amountOptions.map(option => (
            <RadioGroup.Item value={option.value} label={option.label} key={option.value}>
              <MonetaryAmountOption text={option.label} />
            </RadioGroup.Item>
          ))}
          <RadioGroup.Item value="other" label="Other">
            <MonetaryAmountOption text="Other" />
          </RadioGroup.Item>
        </RadioGroup>
        {value === 'other' && (
          <YStack style={styles.inputContainer}>
            <Text weight="semibold">{otherValueLabel}</Text>
            <FormControlTextInput
              placeholder="e.g. $10.50"
              onChangeText={onChangeText}
              value={maskedValue}
              keyboardType="number-pad"
              rightIcon={<IconDollar size={24} color={tokens.colors.$blackOpacity30} />}
              maxLength={7} // Considering spaces and $
              error={errorMessage}
              testID={CUSTOM_MONETARY_INPUT}
            />
          </YStack>
        )}
      </YStack>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 20,
    flex: 1,
  },
  radioGroup: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'space-between',
    flex: 1,
  },
  radioGroupDesktop: {
    flexDirection: 'row',
    gap: 16,
    flex: 1,
  },
  inputContainer: {
    marginTop: 20,
    gap: 8,
  },
  inputContainerDesktop: {
    flex: 1,
    gap: 4,
    marginTop: 20,
  },
  tipSelectorDesktop: {
    alignItems: 'center',
    gap: 16,
  },
  tipContainer: {
    marginTop: 8,
  },
  tipRow: {
    justifyContent: 'space-between',
    marginBottom: 8,
    marginTop: 20,
  },
  tipText: {
    fontSize: 14,
    lineHeight: 18,
  },
  desktopLeftWrapper: {
    flex: 1,
  },
  desltopRightWrapper: {
    flex: 1,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
});
